import React, { useCallback, useRef, useState, CSSProperties, useMemo, useEffect } from 'react';
import { Column, ColumnBodyType, ColumnProps } from 'primereact/column';
import { useUserDatatable } from '../hooks/useUserDatatable';
import { DataTable } from 'primereact/datatable';
import { locationService } from 'app/services/LocationService';
import { OverlayMenu, PrimeTable } from 'components';
import { UserConfiguration } from '../types';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Card, CardBody, Container } from 'reactstrap';
import { useDeleteUserConfigurationMutation } from '../services/userApi';
import { confirmDialog } from 'primereact/confirmdialog';
import { useToasts } from 'app/components/Toast';
import { useAppDispatch } from 'app/store';
import { setIsEditingPage } from 'app/reducers/pageSlice';

export interface Props {
	columns?: ColumnProps[];
}

const UserManagementPage = (props: Props) => {
		
	const dispatch = useAppDispatch()

	useEffect(() => {
		dispatch(setIsEditingPage(false)) // disable editing state in order to show add menu button
	},[dispatch])

	const toast = useToasts();

	const { data, isLoading, totalRecords, rows, first, filters, sortField, sortOrder, onPage, onSort, onFilter } =
		useUserDatatable();

	const [selectedRows, setSelectedRows] = useState(null);
	const [globalFilter, setGlobalFilter] = useState(null);
	const dt = useRef<DataTable>(null);

	const history = locationService.getHistory();

	const _handleEdit = useCallback(
		(id: number | string) => {
			history.push({ pathname: `/user-management/edit/${id}` });
		},
		[history],
	);

	const [deleteApi] = useDeleteUserConfigurationMutation();

	const _handleDelete = useCallback(
		(id: number | string) => {
			confirmDialog({
				message: `Do you want to delete this user with ID: ${id}?`,
				header: 'Delete Confirmation',
				icon: 'pi pi-info-circle',
				acceptClassName: 'p-button-danger',
				accept: async () =>
					deleteApi(id)
						.unwrap()
						.then(() => {
							toast.toastSuccess('Success', `User Management deleted.`);
						})
						.catch((error) => {
							toast.toastError(error?.data?.error, error?.data?.message);
						}),
			});
		},
		[deleteApi, toast],
	);

	const columns: any = useMemo(() => {
		const actionTemplateBody = (rowData: UserConfiguration): ColumnBodyType => {
			const items = [
				{
					label: 'Edit',
					icon: 'mdi mdi-pencil',
					command: () => _handleEdit(rowData.id),
				},
				{
					label: 'Delete',
					icon: 'mdi mdi-delete',
					command: () => _handleDelete(rowData.id),
				},
			];
			return (
				<React.Fragment>
					<OverlayMenu id={`overlay-menu-${rowData.id}`} items={items} />
				</React.Fragment>
			);
		};

		return [
			{
				field: 'id',
				header: 'ID',
			},
			{
				field: 'userInfo.name',
				header: 'NAME',
			},

			{
				field: 'username',
				header: 'USERNAME',
			},
			{
				field: 'userInfo.email',
				header: 'EMAIL',
			},

			{
				field: 'roleNames.0',
				header: 'ROLE',
			},

			{
				field: 'status',
				header: 'STATUS',
			},

			{
				headerStyle: {
					width: '5em',
				} as CSSProperties,
				body: actionTemplateBody,
				bodyStyle: {
					textAlign: 'center',
					overflow: 'visible',
				} as CSSProperties,
			},
		];
	}, [_handleEdit, _handleDelete]);

	const CreateNewUser = () => {
		history.push({
			pathname: '/user-management/new',
		});
	};

	const renderHeader = () => {
		return (
			<div className="table-header">
				<span className="p-input-icon-left">
					<i className="pi pi-search" />
					<InputText
						type="search"
						onInput={(e: any) => setGlobalFilter(e.target.value)}
						placeholder="Search"
					/>
				</span>

				<Button
					label="Add New"
					icon="mdi mdi-plus-circle"
					iconPos="right"
					className="p-button-link text-default"
					onClick={CreateNewUser}
				/>
			</div>
		);
	};

	const renderColumns = () => {
		return columns && columns.map((column: ColumnProps, i) => <Column key={i} {...column} />);
	};

	const header = renderHeader();

	return (
		<Container fluid>
			<h1 className="my-3">User Management</h1>
			<div>
				<Card>
					<CardBody>
						<PrimeTable
							lazy
							ref={dt}
							value={data}
							header={header}
							dataKey="id"
							globalFilter={globalFilter}
							selection={selectedRows}
							onSelectionChange={(e) => setSelectedRows(e.value)}
							rows={rows}
							first={first}
							sortField={sortField}
							sortOrder={sortOrder}
							loading={isLoading}
							totalRecords={totalRecords}
							filters={filters}
							onPage={onPage}
							onSort={onSort}
							onFilter={onFilter}
						>
							{renderColumns()}
						</PrimeTable>
					</CardBody>
				</Card>
			</div>
		</Container>
	);
};

export default UserManagementPage;
